/**
 * Condition group node for the automation flow builder.
 * Displays branches with conditions and their action steps.
 */
import { Badge } from '@/components/ui/badge';
import type { ConditionGroupConfig, ConditionBranch, ActionStep } from '@/lib/dag-types';

const OPERATOR_LABELS: Record<string, string> = {
  gt: '>',
  gte: '>=',
  lt: '<',
  lte: '<=',
  eq: '=',
  neq: '!=',
  in: 'in',
  between: 'between',
};

function ConditionDisplay({ conditions }: { conditions: ConditionBranch['conditions'] }) {
  if (conditions.length === 0) return null;
  return (
    <div className="px-2.5 py-1.5">
      {conditions.map((c, i) => (
        <div key={i} className="text-xs text-wt-text-secondary py-0.5 flex items-center gap-1.5">
          <span className="text-[9px] font-mono text-wt-text-disabled">
            {i === 0 ? 'IF' : 'AND'}
          </span>
          <span>
            {c.field} {OPERATOR_LABELS[c.operator] ?? c.operator}{' '}
            {c.operator === 'between'
              ? `${String(c.value)} – ${String(c.value_end)}`
              : Array.isArray(c.value)
              ? `[${c.value.join(', ')}]`
              : String(c.value)}
          </span>
        </div>
      ))}
    </div>
  );
}

function BranchStepCard({ step }: { step: ActionStep }) {
  const isApproval = step.type === 'approval';
  return (
    <div
      className={`rounded-md p-2 mt-1 border ${
        isApproval
          ? 'bg-wt-bg-tertiary border-wt-amber-primary/30 border-l-[3px] border-l-wt-amber-primary'
          : 'bg-wt-bg-secondary border-wt-border-subtle'
      }`}
    >
      {isApproval && (
        <div className="flex items-center gap-1 mb-1">
          <span className="text-[8px] font-bold uppercase tracking-wider text-wt-amber-primary">AWAIT APPROVAL</span>
        </div>
      )}
      <p className="text-[11px] font-medium text-wt-text-primary">{step.title}</p>
      {step.subtitle && (
        <p className="text-[10px] text-wt-text-muted mt-0.5">{step.subtitle}</p>
      )}
    </div>
  );
}

function BranchCard({ branch, isOtherwise }: { branch: ConditionBranch; isOtherwise: boolean }) {
  return (
    <div className="flex-1 min-w-[200px] bg-wt-bg-primary border border-wt-border-subtle rounded-md overflow-hidden">
      <div className="px-2.5 py-1.5 border-b border-wt-border-subtle flex items-center gap-1.5">
        <span
          className={`text-[9px] font-semibold uppercase tracking-wider ${
            isOtherwise ? 'text-wt-text-muted' : 'text-wt-amber-primary'
          }`}
        >
          {isOtherwise ? 'OTHERWISE' : branch.label}
        </span>
        {!isOtherwise && branch.conditions.length > 0 && (
          <Badge variant="outline" className="text-[9px] h-4 bg-wt-amber-primary/10 border-wt-amber-primary/20 text-wt-amber-primary">
            {branch.conditions.length}
          </Badge>
        )}
      </div>
      {branch.conditions.length > 0 && <ConditionDisplay conditions={branch.conditions} />}
      <div className="px-2.5 pb-2.5 pt-1">
        <span className="text-[9px] font-bold uppercase tracking-wider text-wt-green-primary">THEN</span>
        {branch.then_steps.map((step) => (
          <BranchStepCard key={step.id} step={step} />
        ))}
      </div>
    </div>
  );
}

interface ConditionGroupNodeProps {
  config: ConditionGroupConfig;
  stepNum?: number;
  readOnly?: boolean;
  onClick?: () => void;
}

const OTHERWISE_LABELS = ['Otherwise', 'Grid Available', 'Minor Excess', 'Medium Confidence', 'Daytime Spike'];

export function ConditionGroupNode({ config, stepNum, readOnly, onClick }: ConditionGroupNodeProps) {
  return (
    <div
      className="bg-wt-bg-secondary border border-wt-border-subtle rounded-lg overflow-hidden cursor-pointer hover:border-wt-amber-primary/40 transition-colors"
      onClick={!readOnly ? onClick : undefined}
    >
      <div className="flex items-center gap-2 px-3 py-2 border-b border-wt-border-subtle">
        {stepNum && (
          <span className="w-5 h-5 rounded-full bg-wt-bg-tertiary border border-wt-border-subtle flex items-center justify-center text-[10px] font-semibold font-mono text-wt-text-muted">
            {stepNum}
          </span>
        )}
        <span className="text-[10px] font-bold uppercase tracking-wider text-wt-amber-primary">
          CHECK IF
        </span>
      </div>
      <div className="p-3 flex flex-wrap gap-2.5">
        {config.branches.map((branch) => {
          const isOtherwise = OTHERWISE_LABELS.includes(branch.label) || branch.conditions.length === 0;
          return <BranchCard key={branch.label} branch={branch} isOtherwise={isOtherwise} />;
        })}
      </div>
    </div>
  );
}
